import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Phone, Mail, Calendar, User, Pencil, ShieldOff, ShieldCheck } from 'lucide-react';
import { formatDate } from '../../../lib/utils';
import CustomerAuthInfo from './CustomerAuthInfo';
import CustomerStatsGrid from './CustomerStatsGrid';

export default function CustomerProfileCard({ customerDetails, onEditEmail, onDeactivate, onActivate }) {
  const { customer, stats } = customerDetails;

  return (
    <Card className="p-6">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
          <User className="w-8 h-8 text-primary" />
        </div>
        <div>
          <h2 className="text-2xl font-bold" data-testid="customer-detail-name">{customer.name}</h2>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-xs px-2 py-1 rounded-full ${
              customer.type === 'registered'
                ? 'bg-green-100 text-green-700'
                : 'bg-blue-100 text-blue-700'
            }`}>
              {customer.type === 'registered' ? 'Registered User' : 'Guest'}
            </span>
            {/* Activation badge — only meaningful for registered
                users (guests have no account_status). Green for
                Active, red for Inactive. Mirrors the per-card
                dot indicator in the list view. */}
            {customer.type === 'registered' && (
              <span
                className={`text-xs px-2 py-1 rounded-full font-semibold ${
                  customer.account_status === 'inactive'
                    ? 'bg-rose-100 text-rose-700'
                    : 'bg-emerald-100 text-emerald-700'
                }`}
                data-testid="customer-status-badge"
              >
                {customer.account_status === 'inactive' ? 'Inactive' : 'Active'}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {customer.phone && (
          <div className="flex items-center gap-3">
            <Phone className="w-5 h-5 text-muted-foreground" />
            <span>{customer.phone}</span>
          </div>
        )}
        {/* Email row.
            - Registered users: read-only (their profile is
              managed by the customer via account settings).
            - Guests: staff can edit or add an email. Renders
              an inline pencil button; when there's no email
              yet, shows a subtle "Add email" affordance. */}
        {customer.type === 'guest' ? (
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-muted-foreground" />
            {customer.email ? (
              <span className="flex-1 truncate" data-testid="guest-email-value">
                {customer.email}
              </span>
            ) : (
              <span className="flex-1 text-muted-foreground italic" data-testid="guest-email-empty">
                No email on file
              </span>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onEditEmail}
              className="h-7 px-2 text-primary hover:text-primary"
              data-testid="guest-email-edit-btn"
              title={customer.email ? 'Edit email' : 'Add email'}
            >
              <Pencil className="w-3.5 h-3.5 mr-1" />
              {customer.email ? 'Edit' : 'Add'}
            </Button>
          </div>
        ) : (
          customer.email && (
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-muted-foreground" />
              <span>{customer.email}</span>
            </div>
          )
        )}
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-muted-foreground" />
          <span>Customer since {customer.created_at ? formatDate(customer.created_at) : 'Unknown'}</span>
        </div>

        {customer.type === 'registered' && <CustomerAuthInfo customer={customer} />}
      </div>

      <CustomerStatsGrid stats={stats} />

      {/* Activation toggle — only shown for registered users.
          Destructive variant for active→inactive; primary for
          inactive→active. Click opens the parameterised
          AlertDialog declared at the page root. */}
      {customer.type === 'registered' && (
        <div className="mt-6 pt-6 border-t">
          {customer.account_status === 'inactive' ? (
            <Button
              type="button"
              onClick={onActivate}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="activate-customer-btn"
            >
              <ShieldCheck className="w-4 h-4 mr-2" />
              Activate Customer
            </Button>
          ) : (
            <Button
              type="button"
              variant="destructive"
              onClick={onDeactivate}
              className="w-full"
              data-testid="deactivate-customer-btn"
            >
              <ShieldOff className="w-4 h-4 mr-2" />
              Deactivate Customer
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
